/**
 * Vocalizing a text without changing what it says.
 *
 * Automatic pointing is accurate on rabbinic prose and carries one hazard that
 * would be disqualifying unguarded: **it rewrites the consonantal skeleton as
 * it works**, dropping matres lectionis to produce the defective spelling a
 * vocalized text conventionally uses. That is defensible scholarship and it is
 * not something a publisher may do silently — the letters printed have to be
 * the letters quoted.
 *
 * So nothing here trusts the pointing. Every word is checked by stripping the
 * marks back off and comparing the letters that remain against the letters that
 * went in; a word whose consonants moved is reported and never accepted on its
 * own. The check is the reason the feature is usable, not a precaution around
 * it.
 *
 * Stripping is `stripPointing`'s rather than a second copy: two strippers are
 * free to disagree about what counts as a mark, and a guard that strips
 * differently from the value it compares against reports changes that never
 * happened.
 */
import { stripPointing } from './content'
import { foldCase as fold } from './search'

/** A word whose letters the vocalizer changed, and what it changed them to. */
export type LetterChange = {
  /** Where it is, counted in words over the whole passage. */
  index: number
  before: string
  after: string
}

export type VocalizationCheck = {
  /** Every word whose consonantal skeleton moved. Empty is the clean case. */
  changes: LetterChange[]
  /** True only when nothing moved — the passage may be accepted whole. */
  clean: boolean
}

/**
 * Compare a pointed passage against the text it was pointed from.
 *
 * Word count is compared first because a mismatch makes the pairing
 * meaningless: reporting the wrong words as changed is worse than reporting
 * that the comparison could not be made, so that case throws rather than
 * guessing at an alignment.
 */
export function checkVocalization(
  before: string,
  after: string,
): VocalizationCheck {
  const a = before.split(/\s+/).filter(Boolean)
  const b = after.split(/\s+/).filter(Boolean)

  if (a.length !== b.length) {
    throw new Error(
      `Vocalized text has ${b.length} words against ${a.length} — cannot be compared word for word.`,
    )
  }

  const changes: LetterChange[] = []
  for (const [i, word] of a.entries()) {
    const pointed = b[i] as string
    if (stripPointing(pointed) !== stripPointing(word)) {
      changes.push({ index: i, before: word, after: pointed })
    }
  }
  return { changes, clean: changes.length === 0 }
}

/** How a segment's pointing stands, and how far it may be trusted. */
export type VocalizationState =
  /** As supplied — whatever pointing the source had, ours to neither claim nor check. */
  | 'original'
  /** No marks at all. */
  | 'none'
  /** Machine-pointed and not yet read by anybody. Never printed. */
  | 'auto'
  /** Machine-pointed, letters verified unchanged, and accepted by a person. */
  | 'reviewed'

/**
 * Whether a pointed segment may be promoted to reviewed.
 *
 * Two conditions, and both are required: the letters must be unchanged, and a
 * person must have accepted it. Neither substitutes for the other — the check
 * catches what a reader would not notice, and the reader catches what the check
 * cannot, which is whether the vocalization is actually right.
 */
export function canPromote(input: {
  check: VocalizationCheck
  acceptedBy: string | null
}): boolean {
  return input.check.clean && input.acceptedBy !== null
}

/**
 * Apply a pointing's MARKS without letting it change any words.
 *
 * Word by word: where the letters agree, the pointed form is taken; where they
 * do not, the original stands unpointed. The result therefore has exactly the
 * letters it started with, whatever the vocalizer did — a misquotation becomes
 * impossible rather than merely detectable.
 *
 * That is what lets pointing be a presentation layer with nothing to review.
 * The alternative — showing the vocalizer's output and checking it afterwards —
 * needs a person between the pointing and the reader, because what it gets
 * wrong is silent: a dropped mater lectionis reads as an ordinary spelling.
 *
 * The cost is visible in the right place. A word that could not be pointed
 * without rewriting stays bare, which says plainly that we do not know how to
 * point it without changing it.
 *
 * A word count that does not match means the pairing is meaningless, so the
 * original is returned whole rather than aligned by guesswork.
 */
export function applyMarks(original: string, pointed: string): string {
  const a = original.split(/(\s+)/)
  const b = pointed.split(/(\s+)/)
  if (a.length !== b.length) return original

  return a
    .map((word, i) => {
      const candidate = b[i] as string
      return stripPointing(candidate) === stripPointing(word) ? candidate : word
    })
    .join('')
}

/** How much of a passage a pointing could be applied to, for a reader's notice. */
export function marksApplied(
  original: string,
  pointed: string,
): { pointed: number; kept: number } {
  const a = original.split(/\s+/).filter(Boolean)
  const b = pointed.split(/\s+/).filter(Boolean)
  if (a.length !== b.length) return { pointed: 0, kept: a.length }

  let applied = 0
  for (const [i, word] of a.entries()) {
    if (stripPointing(b[i] as string) === stripPointing(word)) applied += 1
  }
  return { pointed: applied, kept: a.length - applied }
}

/**
 * Where a search term falls inside a passage as it is DISPLAYED.
 *
 * The match is made on letters and the passage may carry marks, so the two
 * strings do not share coordinates: searching the displayed text for the query
 * finds nothing the moment a single vowel sits between two of its letters.
 * The letters are therefore walked with their positions remembered, the match
 * made there, and the answer mapped back.
 *
 * Ranges are half-open and in display coordinates, so a caller slices the text
 * it actually renders.
 *
 * **Case is folded.** Hebrew has none, so this changes nothing for the material
 * the function was written for — but the same search runs over English, where a
 * query is typed the way it is spoken rather than the way the page happens to
 * capitalize. Left cased, a search that FINDS a passage highlights nothing
 * inside it, and the two halves of one search disagree.
 */
export function matchRanges(
  display: string,
  query: string,
): [number, number][] {
  const term = fold(stripPointing(query.trim()))
  if (!term) return []

  // Position of each LETTER within the displayed string.
  const at: number[] = []
  let letters = ''
  for (const [i, ch] of [...display].entries()) {
    const bare = stripPointing(ch)
    if (bare) {
      letters += fold(bare)
      at.push(i)
    }
  }

  const ranges: [number, number][] = []
  let from = 0
  while (true) {
    const found = letters.indexOf(term, from)
    if (found === -1) break
    const start = at[found] as number
    // The end runs to the character before the NEXT letter, so the marks
    // belonging to the last matched letter stay inside the range rather than
    // hanging outside it.
    const afterLast = at[found + term.length]
    ranges.push([start, afterLast ?? display.length])
    from = found + term.length
  }
  return ranges
}

/** A passage split into its matched and unmatched runs, in order. */
export function splitOnMatch(
  display: string,
  query: string,
): { text: string; match: boolean }[] {
  const ranges = matchRanges(display, query)
  if (!ranges.length) return [{ text: display, match: false }]

  const parts: { text: string; match: boolean }[] = []
  let cursor = 0
  for (const [start, end] of ranges) {
    if (start > cursor) {
      parts.push({ text: display.slice(cursor, start), match: false })
    }
    parts.push({ text: display.slice(start, end), match: true })
    cursor = end
  }
  if (cursor < display.length) {
    parts.push({ text: display.slice(cursor), match: false })
  }
  return parts
}
